import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import jsPDF from 'jspdf';
import { formatCurrency, generateDetailedPDF } from '../../../utils/reportUtils';
import printerService from '../../../services/printerService';

const ReporteCaja = ({ shiftId }) => {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const getFastFoodBaseURL = () => process.env.REACT_APP_FAST_FOOD_SERVICE || 'http://localhost:8002';

    useEffect(() => {
        const fetchReport = async () => {
            try {
                const response = await api.get(`/api/pos/shifts/${shiftId}/report/`, {
                    baseURL: getFastFoodBaseURL()
                });
                
                const shiftData = response.data;
                const normalizedReport = {
                    ...shiftData.summary,
                    shift_info: shiftData.shift_info,
                    orders_detail: shiftData.orders_detail,
                    payment_methods: shiftData.payment_methods,
                    top_products: shiftData.top_products,
                    expenses: shiftData.expenses || [],
                    date: shiftData.shift_info.opened_at,
                    is_shift_report: true,
                    generated_by: shiftData.shift_info.user
                };
                
                setReportData(normalizedReport);
            } catch (err) {
                console.error("Error fetching shift report:", err);
                setError('No se pudo cargar el reporte del turno.');
            } finally {
                setLoading(false);
            }
        };

        if (shiftId) fetchReport();
    }, [shiftId]);

    if (loading) return <div style={{ padding: '3rem', textAlign: 'center', color: '#6c757d' }}>Cargando reporte...</div>;
    if (error) return <div style={{ padding: '3rem', textAlign: 'center', color: '#dc3545' }}>{error}</div>;
    if (!reportData) return null;

    const { shift_info } = reportData;
    
    const openingCash = parseFloat(shift_info.opening_cash || 0);
    const closingCash = parseFloat(shift_info.closing_cash || 0); // Este closingCash puede incluir transferencias si fue caja ciega
    const totalSales = parseFloat(reportData.total_sales || 0);
    const totalExpenses = parseFloat(reportData.total_expenses || 0);
    const expensesList = reportData.expenses || [];
    // Separate expenses by currency (tagged with [USD] in description)
    const exchangeRate = parseFloat(localStorage.getItem('usdExchangeRate')) || 4000;
    const expensesUSD = expensesList.filter(e => e.description && e.description.startsWith('[USD]'));
    const expensesCOP = expensesList.filter(e => !e.description || !e.description.startsWith('[USD]'));
    const totalExpensesUSD = expensesUSD.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0) / exchangeRate; // convert back to USD for display
    const totalExpensesCOP = expensesCOP.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);


    // Desglose de métodos de pago (Sistema)
    let paymentStats = {
        efectivo_cop: 0,
        transferencia_cop: 0,
        tarjeta_cop: 0,
        efectivo_usd: 0,
        transferencia_usd: 0,
        tarjeta_usd: 0
    };
    let cancelledStats = { count: 0, total: 0 };
    let employeeStats = { count: 0, total: 0 };
    
    if (reportData.orders_detail && Array.isArray(reportData.orders_detail)) {
        reportData.orders_detail.forEach(order => {
            if (['cancelled'].includes(order.status) || order.status_display?.toLowerCase() === 'anulada') {
                cancelledStats.count += 1;
                cancelledStats.total += parseFloat(order.total_amount || order.total || 0);
            } else if (order.notes && order.notes.includes('[VENTA_EMPLEADO]')) {
                employeeStats.count += 1;
                employeeStats.total += parseFloat(order.total_amount || order.total || 0);
            }
        });
    }

    if (reportData.orders_detail && Array.isArray(reportData.orders_detail)) {
        reportData.orders_detail.forEach(order => {
            if (['delivered', 'completed'].includes(order.status) || order.payment_status === 'paid') {
                const method = String(order.payment_method_display || order.payment_method || '').toLowerCase();
                const total = parseFloat(order.total_amount || order.total || 0);
                
                if (method.includes('dividido')) {
                    let cashUsd = 0;
                    let cashCop = 0;
                    let transUsd = 0;
                    let transCop = 0;
                    
                    if (order.notes && order.notes.includes('[SPLIT]')) {
                        const matchCashUsd = order.notes.match(/CASH_USD=([\d.]+)/);
                        if (matchCashUsd) cashUsd = parseFloat(matchCashUsd[1]);
                        
                        const matchCashCop = order.notes.match(/CASH_COP=([\d.]+)/);
                        if (matchCashCop) cashCop = parseFloat(matchCashCop[1]);
                        
                        const matchTransUsd = order.notes.match(/TRANSFER_USD=([\d.]+)/);
                        if (matchTransUsd) transUsd = parseFloat(matchTransUsd[1]);
                        
                        const matchTransCop = order.notes.match(/TRANSFER_COP=([\d.]+)/);
                        if (matchTransCop) transCop = parseFloat(matchTransCop[1]);
                    } else {
                        // Fallback heredado para órdenes viejas (asume splitCash = cashAmt, etc.)
                        const cashAmt = parseFloat(order.cash_amount || 0);
                        const transAmt = parseFloat(order.transfer_amount || 0);
                        let isDolar = method.includes('dolar');
                        if (isDolar) {
                            cashUsd = cashAmt;
                            transUsd = transAmt;
                        } else {
                            cashCop = cashAmt;
                            transCop = transAmt;
                        }
                    }
                    
                    // Multiplicamos por exchangeRate porque paymentStats.efectivo_usd espera el valor en COP
                    // al igual que los cobros en dolares regulares que se procesan por el total (que está en COP)
                    paymentStats.efectivo_usd += (cashUsd * exchangeRate);
                    paymentStats.efectivo_cop += cashCop;
                    paymentStats.transferencia_usd += (transUsd * exchangeRate);
                    paymentStats.transferencia_cop += transCop;
                } else if (method.includes('dolar') || method.includes('usd')) {
                    if (method.includes('efectivo') || method === 'cash') paymentStats.efectivo_usd += total;
                    else if (method.includes('transferencia') || method.includes('transfer')) paymentStats.transferencia_usd += total;
                    else if (method.includes('tarjeta') || method.includes('card')) paymentStats.tarjeta_usd += total;
                    else paymentStats.efectivo_usd += total;
                } else {
                    if (method.includes('efectivo') || method === 'cash') paymentStats.efectivo_cop += total;
                    else if (method.includes('transferencia') || method.includes('transfer')) paymentStats.transferencia_cop += total;
                    else if (method.includes('tarjeta') || method.includes('card')) paymentStats.tarjeta_cop += total;
                    else paymentStats.efectivo_cop += total;
                }
            }
        });
    }

    // EXTRAER TRANSFERENCIAS Y TARJETAS FÍSICAS DE LAS NOTAS (Caja Ciega)
    let transferenciasFisico = 0;
    let fisicoEfectivoUSD = 0, fisicoTransferUSD = 0, fisicoEfectivoCOP = 0, fisicoTransferCOP = 0;
    let isCierreCiegoV2 = false;
    let cierreCiegoDetalle = "";

    if (shift_info.closing_notes) {
        if (shift_info.closing_notes.includes('[CIERRE_CIEGO_V2]')) {
            isCierreCiegoV2 = true;
            const noteLines = shift_info.closing_notes.split('\n');
            noteLines.forEach(l => {
                if (l.startsWith('FisicoEfectivoUSD=')) fisicoEfectivoUSD = parseFloat(l.split('=')[1]);
                if (l.startsWith('FisicoTransferUSD=')) fisicoTransferUSD = parseFloat(l.split('=')[1]);
                if (l.startsWith('FisicoEfectivoCOP=')) fisicoEfectivoCOP = parseFloat(l.split('=')[1]);
                if (l.startsWith('FisicoTransferCOP=')) fisicoTransferCOP = parseFloat(l.split('=')[1]);
            });
            const parts = shift_info.closing_notes.split('---\n');
            if (parts.length > 1) {
                cierreCiegoDetalle = parts[1];
            }
        } else if (shift_info.closing_notes.includes('Transferencias: $')) {
            const matchT = shift_info.closing_notes.match(/Transferencias:\s*\$?([\d.]+)/);
            if (matchT) {
                transferenciasFisico = parseFloat(matchT[1]);
            }
        }
    }

    const efectivoEsperadoCOP = (openingCash + paymentStats.efectivo_cop) - totalExpenses;
    const efectivoFisicoDeclarado = closingCash - transferenciasFisico;
    const efectivoFisicoReal = efectivoFisicoDeclarado;
    const sobranteEfectivo = efectivoFisicoDeclarado - efectivoEsperadoCOP;
    
    const transferenciasSistemaCOP = paymentStats.transferencia_cop;
    const sobranteTransferencia = transferenciasFisico - transferenciasSistemaCOP;

    // exchangeRate already declared above
    // Los pagos USD en BD están en pesos (COP), hay que convertir a USD real
    const dolaresEfectivoSistema = paymentStats.efectivo_usd / exchangeRate;
    const dolaresTransferenciaSistema = paymentStats.transferencia_usd / exchangeRate;


    const handlePrintCaja = async () => {
        const countSales = reportData.orders_detail ? reportData.orders_detail.length : 0;
        const totalSales = parseFloat(reportData.total_sales || 0);

        const chars_per_line = 54;
        let lines = [];
        
        const center = (text) => {
            const padding = Math.max(0, Math.floor((chars_per_line - text.length) / 2));
            return ' '.repeat(padding) + text;
        };

        const rightAlign = (label, value) => {
            const valueStr = String(value);
            const padding = Math.max(0, chars_per_line - label.length - valueStr.length);
            return label + ' '.repeat(padding) + valueStr;
        };

        const formatRow3 = (label, count, amount) => {
            const countStr = `: ${count}`;
            const amountStr = `(${parseFloat(amount).toFixed(2)})`;
            const labelPad = label.padEnd(22, ' ');
            const countPad = countStr.padEnd(12, ' ');
            const amountPad = amountStr.padStart(20, ' ');
            return labelPad + countPad + amountPad;
        };

        const current_time = new Date();
        const pad0 = (n) => n.toString().padStart(2, '0');
        const dateStr = `${current_time.getFullYear()}-${pad0(current_time.getMonth()+1)}-${pad0(current_time.getDate())} ${pad0(current_time.getHours())}:${pad0(current_time.getMinutes())}:${pad0(current_time.getSeconds())}`;
        
        lines.push(center(`CIERRE DE CAJA #${shift_info.number}`));
        lines.push(center(dateStr));
        lines.push(center(`USUARIO: ${shift_info.user || shift_info.manager_name}`));
        lines.push("");

        if (expensesList.length > 0) {
            lines.push(center("GASTOS DE CAJA"));
            expensesList.forEach(exp => {
                const desc = "- " + exp.description;
                lines.push(rightAlign(desc.length > 30 ? desc.substring(0, 30) : desc, `-${parseFloat(exp.amount).toFixed(2)}`));
            });
            lines.push("-".repeat(chars_per_line));
        }

        lines.push(center("RESUMEN DEL CIERRE"));
        lines.push("-".repeat(chars_per_line));
        
        lines.push(formatRow3("TOTAL VENTAS", countSales, totalSales));
        lines.push(formatRow3("VENTAS ANULADAS", cancelledStats.count, cancelledStats.total));
        lines.push("-".repeat(chars_per_line));
        
        lines.push(rightAlign("EFECTIVO", paymentStats.efectivo.toFixed(2)));
        lines.push(rightAlign("TRANSFERENCIA", paymentStats.transferencia.toFixed(2)));
        lines.push("-".repeat(chars_per_line));
        
        lines.push(formatRow3("TOTAL GASTOS", expensesList.length, `-${totalExpenses.toFixed(2)}`));
        lines.push(formatRow3("TOTAL INGRESOS", 0, "0.00"));
        lines.push(formatRow3("TOTAL OTROS/EXTRAS", 0, "0.00"));
        lines.push("");

        lines.push(center("-------DESGLOCE TRIBUTARIO VENTAS-------"));
        lines.push(formatRow3("NOTAS DE ENTREGA", countSales, totalSales));
        lines.push(formatRow3("FACTURAS", 0, "0.00"));
        lines.push(rightAlign("      BASE 0% :", totalSales.toFixed(2)));
        lines.push(rightAlign("      BASE IVA :", "0.00"));
        lines.push(rightAlign("      IVA :", "0.00"));
        lines.push(rightAlign("      TOTAL :", totalSales.toFixed(2)));
        lines.push("");

        lines.push(center("CAJA"));
        lines.push("-".repeat(chars_per_line));
        
        // FORMATO CLASICO (Como antes, pero con el desglose de GASTOS en SISTEMA)
        lines.push("[EFECTIVO]");
        
        const efectivoTotalBruto = openingCash + paymentStats.efectivo_cop;
        
        // Si hay gastos, mostramos el desglose
        if (totalExpenses > 0) {
            lines.push(rightAlign("SISTEMA", efectivoTotalBruto.toFixed(2)));
            lines.push(rightAlign("GASTOS", totalExpenses.toFixed(2)));
        }
        
        const difEfectivo = efectivoFisicoDeclarado - efectivoEsperadoCOP;
        
        lines.push(rightAlign(totalExpenses > 0 ? "ESPERADO" : "SISTEMA", efectivoEsperadoCOP.toFixed(2)));
        lines.push(rightAlign("CONTEO FISICO", efectivoFisicoDeclarado.toFixed(2)));
        lines.push(rightAlign("DIFERENCIA", difEfectivo.toFixed(2)));
        lines.push("");

        lines.push("[TRANSFERENCIA]");
        lines.push(rightAlign("SISTEMA", transferenciasSistemaCOP.toFixed(2)));
        lines.push(rightAlign("CONTEO FISICO", transferenciasFisico.toFixed(2)));
        lines.push(rightAlign("DIFERENCIA", sobranteTransferencia.toFixed(2)));
        lines.push("");

        const totalSistema = efectivoEsperadoCOP + transferenciasSistemaCOP;
        const totalConteo = efectivoFisicoDeclarado + transferenciasFisico;
        const totalDiferencia = difEfectivo + sobranteTransferencia;

        lines.push("RESUMEN:");
        lines.push(rightAlign("SISTEMA", totalSistema.toFixed(2)));
        lines.push(rightAlign("CONTEO FISICO", totalConteo.toFixed(2)));
        lines.push(rightAlign("DIFERENCIA", totalDiferencia.toFixed(2)));
        lines.push("");

        if (shift_info.closing_notes) {
            lines.push("NOTAS DE CIERRE:");
            const shortNotes = shift_info.closing_notes.split('---')[0].trim();
            if (shortNotes) {
                const noteLines = shortNotes.split('\n');
                noteLines.forEach(l => {
                    if (!l.includes('COP') && !l.includes('[CIERRE_CIEGO_V2]')) {
                        lines.push(l);
                    }
                });
            }
            
            const parts = shift_info.closing_notes.split('---');
            if (parts.length > 1) {
                const cierreCiegoDetalle = parts[1];
                lines.push("-".repeat(chars_per_line));
                const detLines = cierreCiegoDetalle.split('\n');
                let horiz = [];
                detLines.forEach(l => {
                    const match = l.match(/([^:]+):\s*(\d+)/);
                    if (match && !l.includes('USD') && !l.includes('COP') && !l.includes('Fisico')) {
                        let name = match[1].trim().replace('Billetes de ', 'B.');
                        horiz.push(`${name}=${match[2]}`);
                    }
                });
                if (horiz.length > 0) {
                    lines.push("DETALLE DE MONEDAS:");
                    let outLine = "";
                    horiz.forEach(item => {
                        if ((outLine + item).length > chars_per_line) {
                            lines.push(outLine.trim());
                            outLine = "";
                        }
                        outLine += item + "  ";
                    });
                    if (outLine) lines.push(outLine.trim());
                }
            }
        }
        
        lines.push("");
        lines.push(center("Sistema desarrollado por KOA"));
        lines.push(center("Inf: +593 994712899"));

        const ticketContent = lines.join("\n");

        try {
            await printerService.printCustomTicket(ticketContent, 'report');
        } catch (error) {
            alert('Error al enviar impresión. Revisa la consola para más detalles.');
        }
    };

    const handleDownloadPDFCaja = () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const centerX = pageWidth / 2;
        
        let y = 15;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text('RESUMEN DE CAJA', centerX, y, { align: 'center' });
        
        y += 8;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        
        const current_time = new Date();
        const pad0 = (n) => n.toString().padStart(2, '0');
        const dateStr = `${current_time.getFullYear()}-${pad0(current_time.getMonth()+1)}-${pad0(current_time.getDate())} ${pad0(current_time.getHours())}:${pad0(current_time.getMinutes())}:${pad0(current_time.getSeconds())}`;
        
        doc.text(`CÓDIGO DE CAJA: #${shift_info.number}`, centerX, y, { align: 'center' });
        y += 6;
        doc.text(`FECHA / HORA: ${dateStr}`, centerX, y, { align: 'center' });
        y += 6;
        doc.text(`USUARIO: ${shift_info.user || shift_info.manager_name}`, centerX, y, { align: 'center' });
        
        y += 8;
        doc.setLineWidth(0.5);
        doc.line(15, y, pageWidth - 15, y);
        y += 8;

        // Two Column Layout for USD and COP
        const col1X = 15;
        const col2X = centerX + 5;
        const colWidth = (pageWidth - 40) / 2;
        
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('DÓLARES (USD)', col1X, y);
        doc.text('PESOS (COP)', col2X, y);
        y += 2;
        
        let leftY = y + 5;
        let rightY = y + 5;

        // ================= LEFT COLUMN (USD) =================
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('RESUMEN DEL CIERRE', col1X, leftY);
        leftY += 5;
        doc.setFont('helvetica', 'normal');
        const totalSalesUSD = (paymentStats.efectivo_usd + paymentStats.transferencia_usd + paymentStats.tarjeta_usd) / exchangeRate;
        doc.text(`Total Ventas:`, col1X, leftY);
        doc.text(`$${totalSalesUSD.toFixed(2)}`, col1X + colWidth, leftY, { align: 'right' });
        leftY += 5;
        doc.text(`Efectivo:`, col1X, leftY);
        doc.text(`$${(paymentStats.efectivo_usd / exchangeRate).toFixed(2)}`, col1X + colWidth, leftY, { align: 'right' });
        leftY += 5;
        doc.text(`Transferencia:`, col1X, leftY);
        doc.text(`$${(paymentStats.transferencia_usd / exchangeRate).toFixed(2)}`, col1X + colWidth, leftY, { align: 'right' });
        leftY += 8;
        
        doc.setFont('helvetica', 'bold');
        doc.text('ARQUEO DE CAJA', col1X, leftY);
        leftY += 5;
        doc.setFont('helvetica', 'normal');
        doc.text(`Efec. Sistema:`, col1X, leftY);
        doc.text(`$${dolaresEfectivoSistema.toFixed(2)}`, col1X + colWidth, leftY, { align: 'right' });
        leftY += 5;
        doc.text(`Efec. Físico:`, col1X, leftY);
        doc.text(`$${fisicoEfectivoUSD.toFixed(2)}`, col1X + colWidth, leftY, { align: 'right' });
        leftY += 5;
        doc.text(`Dif. Efectivo:`, col1X, leftY);
        doc.text(`${(fisicoEfectivoUSD - dolaresEfectivoSistema) >= 0 ? '+' : ''}$${(fisicoEfectivoUSD - dolaresEfectivoSistema).toFixed(2)}`, col1X + colWidth, leftY, { align: 'right' });
        leftY += 5;
        doc.text(`Transf. Sistema:`, col1X, leftY);
        doc.text(`$${dolaresTransferenciaSistema.toFixed(2)}`, col1X + colWidth, leftY, { align: 'right' });
        leftY += 5;
        doc.text(`Transf. Físico:`, col1X, leftY);
        doc.text(`$${fisicoTransferUSD.toFixed(2)}`, col1X + colWidth, leftY, { align: 'right' });
        leftY += 5;
        doc.text(`Dif. Transf:`, col1X, leftY);
        doc.text(`${(fisicoTransferUSD - dolaresTransferenciaSistema) >= 0 ? '+' : ''}$${(fisicoTransferUSD - dolaresTransferenciaSistema).toFixed(2)}`, col1X + colWidth, leftY, { align: 'right' });
        leftY += 8;

        // ================= RIGHT COLUMN (COP) =================
        doc.setFont('helvetica', 'bold');
        doc.text('RESUMEN DEL CIERRE', col2X, rightY);
        rightY += 5;
        doc.setFont('helvetica', 'normal');
        const totalSalesCOP = paymentStats.efectivo_cop + paymentStats.transferencia_cop + paymentStats.tarjeta_cop;
        doc.text(`Total Ventas:`, col2X, rightY);
        doc.text(`$${totalSalesCOP.toFixed(2)}`, col2X + colWidth, rightY, { align: 'right' });
        rightY += 5;
        doc.text(`Efectivo:`, col2X, rightY);
        doc.text(`$${paymentStats.efectivo_cop.toFixed(2)}`, col2X + colWidth, rightY, { align: 'right' });
        rightY += 5;
        doc.text(`Transferencia:`, col2X, rightY);
        doc.text(`$${paymentStats.transferencia_cop.toFixed(2)}`, col2X + colWidth, rightY, { align: 'right' });
        rightY += 8;

        doc.setFont('helvetica', 'bold');
        doc.text('ARQUEO DE CAJA', col2X, rightY);
        rightY += 5;
        doc.setFont('helvetica', 'normal');
        doc.text(`Efec. Sistema:`, col2X, rightY);
        doc.text(`$${efectivoEsperadoCOP.toFixed(2)}`, col2X + colWidth, rightY, { align: 'right' });
        rightY += 5;
        doc.text(`Efec. Físico:`, col2X, rightY);
        const fisicoCOP = isCierreCiegoV2 ? fisicoEfectivoCOP : efectivoFisicoDeclarado;
        doc.text(`$${fisicoCOP.toFixed(2)}`, col2X + colWidth, rightY, { align: 'right' });
        rightY += 5;
        const difCOP = fisicoCOP - efectivoEsperadoCOP;
        doc.text(`Dif. Efectivo:`, col2X, rightY);
        doc.text(`${difCOP >= 0 ? '+' : ''}$${difCOP.toFixed(2)}`, col2X + colWidth, rightY, { align: 'right' });
        rightY += 5;
        doc.text(`Transf. Sistema:`, col2X, rightY);
        doc.text(`$${transferenciasSistemaCOP.toFixed(2)}`, col2X + colWidth, rightY, { align: 'right' });
        rightY += 5;
        doc.text(`Transf. Físico:`, col2X, rightY);
        const transFisicoCOP = isCierreCiegoV2 ? fisicoTransferCOP : transferenciasFisico;
        doc.text(`$${transFisicoCOP.toFixed(2)}`, col2X + colWidth, rightY, { align: 'right' });
        rightY += 5;
        const difTransCOP = transFisicoCOP - transferenciasSistemaCOP;
        doc.text(`Dif. Transf:`, col2X, rightY);
        doc.text(`${difTransCOP >= 0 ? '+' : ''}$${difTransCOP.toFixed(2)}`, col2X + colWidth, rightY, { align: 'right' });
        rightY += 8;

        // =================== LISTADO DE MONEDAS Y BILLETES ===================
        
        // Denomination value map for the REAL V2 format:
        // USD lines look like: "  5c: 2", "  $1: 1", "  Billetes de $5: 1"
        // COP lines look like: "  $500: 6", "  Billetes de $5mil: 4"
        const USD_DENOM_MAP = {
            '1c': 0.01, '5c': 0.05, '10c': 0.10, '25c': 0.25, '50c': 0.50,
            '$1': 1, '$5': 5, '$10': 10, '$20': 20, '$50': 50, '$100': 100,
            'Billetes de $5': 5, 'Billetes de $10': 10, 'Billetes de $20': 20,
            'Billetes de $50': 50, 'Billetes de $100': 100
        };
        const COP_DENOM_MAP = {
            '$50': 50, '$100': 100, '$200': 200, '$500': 500, '$1000': 1000,
            'Billetes de $2mil': 2000, 'Billetes de $5mil': 5000,
            'Billetes de $10mil': 10000, 'Billetes de $20mil': 20000,
            'Billetes de $50mil': 50000, 'Billetes de $100mil': 100000
        };

        // Helper: parse real V2 lines (format "  5c: 2" or "  Billetes de $5: 1")
        // Returns array of { label, value, qty, subtotal } or { isTransfer, amount }
        const parseV2Lines = (lines, currency) => {
            const items = [];
            const denomMap = currency === 'USD' ? USD_DENOM_MAP : COP_DENOM_MAP;
            lines.forEach(line => {
                const str = line.trim();
                if (!str) return;
                // Transfer line: "Transferencias USD: 15.00" or "Transferencias COP: 3000"
                if (str.toLowerCase().includes('transferencia')) {
                    const match = str.match(/:\s*\$?([\d.]+)/);
                    if (match) {
                        const amount = parseFloat(match[1]);
                        if (amount > 0) items.push({ isTransfer: true, amount, label: str });
                    }
                    return;
                }
                // Denomination line: "  5c: 2" or "  Billetes de $5: 1" or "  $500: 6"
                // Format is "label: qty"
                const colonIdx = str.lastIndexOf(':');
                if (colonIdx === -1) return;
                const label = str.substring(0, colonIdx).trim();
                const qty = parseInt(str.substring(colonIdx + 1).trim());
                if (!qty || qty === 0) return;
                // Find value from map
                const value = denomMap[label];
                if (value !== undefined) {
                    items.push({ isTransfer: false, label, value, qty, subtotal: value * qty });
                }
            });
            return items;
        };

        // Helper: format numbers
        const fmtCOP = (n) => '$' + Math.round(n).toLocaleString('es-CO');
        const fmtUSD = (n) => '$' + n.toFixed(2);

        // Helper: render denomination breakdown in PDF
        const renderDenomBreakdown = (rawLines, currency, colX, colW, startY) => {
            let currentY = startY;
            const items = parseV2Lines(rawLines, currency);
            let physicalTotal = 0;
            let physicalTransfer = 0;

            doc.setFont('helvetica', 'bold');
            doc.text('LISTADO FÍSICO:', colX, currentY);
            currentY += 5;
            doc.setFont('helvetica', 'normal');

            if (items.length === 0) {
                doc.text('(Sin denominaciones registradas)', colX, currentY);
                currentY += 5;
            }

            items.forEach(item => {
                if (currentY > 275) { doc.addPage(); currentY = 15; }
                if (item.isTransfer) {
                    physicalTransfer += item.amount;
                    const fmt = currency === 'USD' ? fmtUSD(item.amount) : fmtCOP(item.amount);
                    doc.text('Transferencia:', colX, currentY);
                    doc.text(fmt, colX + colW, currentY, { align: 'right' });
                    currentY += 5;
                } else {
                    physicalTotal += item.subtotal;
                    const qtyStr = `× ${item.qty}`;
                    const subtotalStr = currency === 'USD' ? fmtUSD(item.subtotal) : fmtCOP(item.subtotal);
                    doc.text(item.label, colX, currentY);
                    doc.text(qtyStr, colX + colW * 0.55, currentY, { align: 'center' });
                    doc.text(subtotalStr, colX + colW, currentY, { align: 'right' });
                    currentY += 5;
                }
            });

            // Total line
            doc.setDrawColor(180, 180, 180);
            doc.setLineWidth(0.3);
            doc.line(colX, currentY, colX + colW, currentY);
            currentY += 4;
            doc.setFont('helvetica', 'bold');
            doc.text('TOTAL FÍSICO:', colX, currentY);
            const totalFmt = currency === 'USD' ? fmtUSD(physicalTotal + physicalTransfer) : fmtCOP(physicalTotal + physicalTransfer);
            doc.text(totalFmt, colX + colW, currentY, { align: 'right' });
            doc.setFont('helvetica', 'normal');
            currentY += 7;

            return currentY;
        };

        // Parse usdLines and copLines from closing notes
        let usdLines = [];
        let copLines = [];

        if (isCierreCiegoV2 && cierreCiegoDetalle) {
            const linesDet = cierreCiegoDetalle.split('\n');
            let isUSD = false, isCOP = false;
            linesDet.forEach(l => {
                if(l.includes('DOLARES (USD):')) { isUSD = true; isCOP = false; }
                else if(l.includes('PESOS (COP):')) { isCOP = true; isUSD = false; }
                else if (l.trim() !== '') {
                    if(isUSD) usdLines.push(l.trim());
                    if(isCOP) copLines.push(l.trim());
                }
            });
        } else if (shift_info.closing_notes && shift_info.closing_notes.includes('Cierre Ciego')) {
            const oldNotes = shift_info.closing_notes.replace('Cierre Ciego. Desglose:\n', '').replace('Cierre Ciego.\n', '');
            const linesDet = oldNotes.split('\n');
            let isUSD = false, isCOP = false;
            linesDet.forEach(l => {
                if (l.includes('DOLARES (USD):')) { isUSD = true; isCOP = false; }
                else if (l.includes('PESOS (COP):')) { isCOP = true; isUSD = false; }
                else if (l.trim() !== '') {
                    if (l.startsWith('- Monedas:') || l.startsWith('- Billetes:') || l.startsWith('- Transferencias')) {
                        if(isUSD) usdLines.push(l.trim());
                        if(isCOP) copLines.push(l.trim());
                    } else {
                        // old individual item format: "5c(1)"
                        if(isUSD) usdLines.push(l.trim());
                        if(isCOP) copLines.push(l.trim());
                    }
                }
            });
        }

        // Render denomination breakdowns
        if (usdLines.length > 0 || copLines.length > 0) {
            if (leftY > 260 || rightY > 260) { doc.addPage(); leftY = 15; rightY = 15; }
            if (usdLines.length > 0) {
                leftY = renderDenomBreakdown(usdLines, 'USD', col1X, colWidth, leftY);
            }
            if (copLines.length > 0) {
                rightY = renderDenomBreakdown(copLines, 'COP', col2X, colWidth, rightY);
            }
        }

        // Global info at the bottom
        let finalY = Math.max(leftY, rightY) + 10;
        if (finalY > 270) { doc.addPage(); finalY = 15; }
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.line(15, finalY, pageWidth - 15, finalY);
        finalY += 8;
        
        doc.setFont('helvetica', 'bold');
        doc.text('OTROS DATOS Y GASTOS', 15, finalY);
        finalY += 6;
        doc.setFont('helvetica', 'normal');
        if (expensesList.length > 0) {
            expensesList.forEach(exp => {
                doc.text(`- Gasto: ${exp.description}`, 15, finalY);
                doc.text(`-$${parseFloat(exp.amount).toFixed(2)}`, pageWidth - 15, finalY, { align: 'right' });
                finalY += 5;
            });
        } else {
            doc.text('No se registraron gastos en el turno.', 15, finalY);
        }

        finalY += 15;
        if (finalY > 280) { doc.addPage(); finalY = 15; }
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text('Sistema desarrollado por KOA', pageWidth / 2, finalY, { align: 'center' });
        doc.text('Inf: +593 994712899', pageWidth / 2, finalY + 4, { align: 'center' });

        doc.save(`Cierre_Caja_${shift_info.number}_A4.pdf`);
    };

    const handlePrintVentas = () => {
        generateDetailedPDF(reportData, 'Reporte de Ventas', `Turno #${shift_info.number}`);
    };

    const styles = {
        container: { fontFamily: 'inherit' },
        headerActions: { display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #e9ecef' },
        titleBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' },
        buttonGroup: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
        titleInfo: { fontSize: '1.1rem', fontWeight: '600', color: '#1a1a2e', marginBottom: '0.75rem' },
        textRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', color: '#495057' },
        textTotal: { display: 'flex', justifyContent: 'space-between', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px dashed #ced4da', fontWeight: '700', color: '#1a1a2e' },
        section: { backgroundColor: '#f8f9fa', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' },
        btnPDFCaja: { 
            backgroundColor: 'var(--primary-color)', color: '#fff', border: 'none', padding: '0.6rem 1rem', 
            borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1'
        },
        btnDownloadPDFCaja: { 
            backgroundColor: '#0d6efd', color: '#fff', border: 'none', padding: '0.6rem 1rem', 
            borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1'
        },
        btnPDFVentas: { 
            backgroundColor: 'var(--secondary-color)', color: 'var(--primary-color)', border: 'none', padding: '0.6rem 1rem', 
            borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1'
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.headerActions}>
                <div style={styles.titleBar}>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1a1a2e' }}>Turno: {shift_info.number}</h2>
                </div>
                <div style={styles.buttonGroup}>
                    <button onClick={handleDownloadPDFCaja} style={styles.btnDownloadPDFCaja}>
                        <i className="bi bi-file-pdf"></i> PDF de Caja
                    </button>
                    <button onClick={handlePrintVentas} style={styles.btnPDFVentas}>
                        <i className="bi bi-file-pdf"></i> PDF de Ventas
                    </button>
                    <button onClick={handlePrintCaja} style={styles.btnPDFCaja}>
                        <i className="bi bi-printer"></i> Imprimir Ticket
                    </button>
                </div>
            </div>

            <div style={styles.section}>
                <h3 style={styles.titleInfo}>Información General</h3>
                <div style={styles.textRow}><span>Encargado:</span> <strong>{shift_info.user || shift_info.manager_name}</strong></div>
                <div style={styles.textRow}><span>Apertura:</span> <span>{new Date(shift_info.opened_at).toLocaleString()}</span></div>
                <div style={styles.textRow}><span>Cierre:</span> <span>{shift_info.closed_at ? new Date(shift_info.closed_at).toLocaleString() : 'En curso'}</span></div>
            </div>

            {/* SECCIÓN DE EFECTIVO */}
            <div style={{ backgroundColor: '#e3f2fd', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
                <h3 style={{ ...styles.titleInfo, color: '#0d47a1' }}>Efectivo</h3>
                <div style={styles.textRow}>
                    <span>Efectivo Inicial:</span> 
                    <span>${openingCash.toFixed(2)}</span>
                </div>
                <div style={styles.textRow}>
                    <span>Ventas del Turno (COP + USD conv):</span> 
                    <span>${(paymentStats.efectivo_cop + (paymentStats.efectivo_usd * exchangeRate)).toFixed(2)}</span>
                </div>
                <div style={{ ...styles.textTotal, borderColor: '#0d47a1', color: '#0d47a1', fontSize: '1.1rem' }}>
                    <span>Total Efectivo Esperado:</span> 
                    <span>${efectivoEsperadoCOP.toFixed(2)}</span>
                </div>
                
                <div style={{ ...styles.textRow, marginTop: '1rem' }}>
                    <span>Efec. Declarado (Cajero):</span> 
                    <span>${efectivoFisicoDeclarado.toFixed(2)}</span>
                </div>
                <div style={styles.textRow}>
                    <span>Menos Gastos (Egresos):</span> 
                    <span style={{ color: 'var(--danger-color)' }}>-${totalExpenses.toFixed(2)}</span>
                </div>
                <div style={{ ...styles.textTotal, borderColor: '#0d47a1', color: '#0d47a1', fontSize: '1.1rem', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                    <span>Efectivo Físico Real:</span> 
                    <span>${efectivoFisicoReal.toFixed(2)}</span>
                </div>
                <div style={{ ...styles.textTotal, border: 'none', color: sobranteEfectivo >= 0 ? 'var(--success-color)' : 'var(--danger-color)', fontSize: '1.1rem', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                    <span>SOBRANTE / FALTANTE EFEC.:</span> 
                    <span>{sobranteEfectivo >= 0 ? '+' : ''}${sobranteEfectivo.toFixed(2)}</span>
                </div>
            </div>

            {/* SECCIÓN DE TRANSFERENCIAS */}
            <div style={{ backgroundColor: '#e8f5e9', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
                <h3 style={{ ...styles.titleInfo, color: '#1b5e20' }}>Transferencias</h3>
                <div style={{ ...styles.textRow, fontWeight: 'bold' }}>
                    <span>Transferencias en Sistema (COP):</span> 
                    <span>${transferenciasSistemaCOP.toFixed(2)}</span>
                </div>
                <div style={{ ...styles.textRow, fontWeight: 'bold' }}>
                    <span>Transferencias en Físico (Contado):</span> 
                    <span>${transferenciasFisico.toFixed(2)}</span>
                </div>
                <div style={{ ...styles.textRow, fontWeight: 'bold', color: sobranteTransferencia >= 0 ? '#1b5e20' : 'var(--danger-color)' }}>
                    <span>Sobrante / Faltante Transferencias:</span> 
                    <span>{sobranteTransferencia >= 0 ? '+' : ''}${sobranteTransferencia.toFixed(2)}</span>
                </div>
            </div>

            <div style={styles.section}>
                <h3 style={styles.titleInfo}>Total General de Ventas (Todos los métodos)</h3>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--primary-color)', textAlign: 'center' }}>
                    ${totalSales.toFixed(2)}
                </div>
            </div>

            {/* SECCIÓN DE VENTAS ANULADAS */}
            <div style={{ backgroundColor: '#fee2e2', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
                <h3 style={{ ...styles.titleInfo, color: '#991b1b' }}>Ventas Anuladas</h3>
                <div style={{ ...styles.textRow, fontWeight: 'bold' }}>
                    <span>Cantidad de Ventas Anuladas:</span> 
                    <span>{cancelledStats.count}</span>
                </div>
                <div style={{ ...styles.textRow, fontWeight: 'bold', color: '#991b1b' }}>
                    <span>Total Ventas Anuladas:</span> 
                    <span>${cancelledStats.total.toFixed(2)}</span>
                </div>
            </div>

            {/* SECCIÓN DE VENTAS A EMPLEADOS */}
            <div style={{ backgroundColor: '#e0e7ff', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
                <h3 style={{ ...styles.titleInfo, color: '#3730a3' }}>Ventas a Empleados</h3>
                <div style={{ ...styles.textRow, fontWeight: 'bold' }}>
                    <span>Cantidad de Ventas:</span> 
                    <span>{employeeStats.count}</span>
                </div>
                <div style={{ ...styles.textRow, fontWeight: 'bold', color: '#3730a3' }}>
                    <span>Total Ventas:</span> 
                    <span>${employeeStats.total.toFixed(2)}</span>
                </div>
            </div>

            {/* GASTOS */}
            {expensesList.length > 0 && (
                <div style={styles.section}>
                    <h3 style={styles.titleInfo}>Detalle de Gastos</h3>
                    {expensesList.map(exp => (
                        <div key={exp.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid #dee2e6' }}>
                            <span style={{ color: '#495057' }}>{exp.description} <br/><small style={{color: '#6c757d'}}>{new Date(exp.created_at).toLocaleTimeString()}</small></span>
                            <span style={{ fontWeight: '600', color: 'var(--danger-color)' }}>${parseFloat(exp.amount).toFixed(2)}</span>
                        </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontWeight: 'bold' }}>
                        <span>TOTAL GASTOS</span>
                        <span style={{ color: 'var(--danger-color)' }}>${totalExpenses.toFixed(2)}</span>
                    </div>
                </div>
            )}
            
            {/* NOTAS */}
            {(shift_info.opening_notes || shift_info.closing_notes) && (
                <div style={{ ...styles.section, backgroundColor: '#fff3cd' }}>
                    <h3 style={{ ...styles.titleInfo, color: '#664d03' }}>Notas</h3>
                    {shift_info.opening_notes && <div style={{ marginBottom: '0.5rem', color: '#664d03' }}><strong>Apertura:</strong> {shift_info.opening_notes}</div>}
                    {shift_info.closing_notes && <div style={{ color: '#664d03', whiteSpace: 'pre-wrap' }}><strong>Cierre:</strong><br/>{shift_info.closing_notes}</div>}
                </div>
            )}
        </div>
    );
};

export default ReporteCaja;
